// The Web Push wire contract — the ONE JSON body the worker's push channel
// encrypts (`apps/worker/src/notifications/web-push.ts`) and the dashboard
// service worker reads (`apps/dashboard/public/sw.js`).
//
// It lives in domain because the two ends are different APPS and apps never
// import each other, so neither could own the shape — and they drifted: the
// sender emitted `{title, body, deepLink, kind}` while `sw.js` read
// `payload.url` / `payload.tag`, so EVERY click landed on `/` and nothing ever
// collapsed. Both ends are now asserted against this module plus the golden
// sample in `@developerz/domain/testing`.
//
// `sw.js` is plain JS served verbatim from `public/` (no build step), so it
// cannot import this — it re-states the field names, and the dashboard's
// contract test runs the REAL file against the shared sample to keep it honest.

import { z } from 'zod';
import { AppRelativePathSchema } from './app-path';

export const PushPayloadSchema = z.object({
  /** Notification title (the fanout event's `title`). */
  title: z.string().min(1).max(200),
  /** Preview line under the title (the event's `body`). */
  body: z.string().min(1).max(1000),
  /** App-relative path `notificationclick` opens (the event's `deepLink`). */
  url: AppRelativePathSchema,
  /**
   * Collapse key — the event's `dedupeKey`. Same tag ⇒ the arriving notification
   * REPLACES the visible one instead of stacking a duplicate. ABSENT when the
   * event carries no dedupeKey: with no stable identity, collapsing would hide
   * genuinely distinct events rather than de-duplicate one.
   */
  tag: z.string().min(1).max(200).optional(),
});
export type PushPayload = z.infer<typeof PushPayloadSchema>;

/** What a channel knows about the notification it is about to push. */
export type PushPayloadInput = {
  title: string;
  body: string;
  /** App-relative path — `NotificationEvent.deepLink`. */
  deepLink: string;
  /**
   * The dashboard's `/org/<segment>` for the account the notification is about:
   * its GitHub login, or its account id when it has none (the API resolves both).
   * REQUIRED: the service worker opens `url` with no page context, so a bare path
   * lands in the session's default org, which may not be this one (plan 101 07-1).
   */
  org: string;
  /** The event's collapse key, when it has one. */
  dedupeKey?: string | undefined;
};

const ORG_PREFIX = '/org/';

/**
 * Scope an app path to an org: `/fleet` → `/org/<org>/fleet`, `/` → `/org/<org>`,
 * and a path already under `/org/` is kept. The same rule as the dashboard's
 * `orgDeepLink` (`apps/dashboard/src/lib/org.ts`), which apps cannot share.
 */
function orgScopedPath(path: string, org: string): string {
  if (path.startsWith(ORG_PREFIX)) return path;
  const base = `${ORG_PREFIX}${encodeURIComponent(org)}`;
  return path === '/' ? base : `${base}${path}`;
}

/**
 * Build the wire body. `deepLink` scoped to `org` → `url` and `dedupeKey` → `tag`
 * is the whole translation: the sender speaks the fanout event's vocabulary, the
 * wire speaks the Notification API's. A missing `dedupeKey` omits `tag` entirely
 * (JSON has no `undefined`) — which is exactly "this one never collapses".
 */
export function buildPushPayload(input: PushPayloadInput): PushPayload {
  const base = {
    title: input.title,
    body: input.body,
    url: orgScopedPath(input.deepLink, input.org),
  };
  return input.dedupeKey ? { ...base, tag: input.dedupeKey } : base;
}
